import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, getFormatConfig, initializeFormats } from '../../../utils/global';
import { useApHub } from '../ApHub/ApHubContext';
import ApHubBreadcrumb from '../ApHub/ApHubBreadcrumb';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const BudgetVsPaymentIntelligence = () => {
  const tenantId = getTenantId();
  const config = getFormatConfig();
  const { selectedBusinessUnitId } = useApHub();

  const [view, setView] = useState('MTD');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState('');

  const hasRequiredFilters = Boolean(selectedBusinessUnitId);

  const loadReport = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!tenantId || !hasRequiredFilters) {
      setReport(null);
      if (!silent) setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await apiFetch('conductor/ap-reports/budget-vs-payment-intelligence', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          view,
          businessUnitId: selectedBusinessUnitId,
        }),
      });
      setReport(data);
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load budget vs Payment Intelligence report');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId, hasRequiredFilters, selectedBusinessUnitId, view]);

  useEffect(() => {
    if (tenantId) {
      initializeFormats(tenantId);
    }
    loadReport();
  }, [tenantId, loadReport]);

  const rows = useMemo(() => {
    const list = Array.isArray(report?.rows) ? report.rows : [];
    return list.map((row, index) => ({
      id: row.glAccountId || `${row.glCode || 'row'}-${index}`,
      glCode: row.glCode || '',
      glAccountName: row.glAccountName || '',
      budgetAmount: Number(row.budgetAmount) || 0,
      sageActualsAmount: Number(row.sageActualsAmount) || 0,
      paymentIntelligenceAmount: Number(row.paymentIntelligenceAmount) || 0,
      variance: Number(row.variance) || 0,
      variancePct: row.variancePct == null ? null : Number(row.variancePct),
    }));
  }, [report]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => (
      (row.glCode || '').toLowerCase().includes(q)
      || (row.glAccountName || '').toLowerCase().includes(q)
    ));
  }, [rows, search]);

  const columns = useMemo(() => [
    {
      field: 'glCode',
      headerName: 'GL Code',
      flex: 0.9,
      minWidth: 120,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'glAccountName',
      headerName: 'GL Account',
      flex: 1.5,
      minWidth: 180,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'budgetAmount',
      headerName: 'Budget',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
    {
      field: 'sageActualsAmount',
      headerName: 'Actuals (Sage)',
      flex: 1,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
    {
      field: 'paymentIntelligenceAmount',
      headerName: 'Payment Intelligence',
      flex: 1.1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
    {
      field: 'variance',
      headerName: 'Variance',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
    {
      field: 'variancePct',
      headerName: 'Variance %',
      flex: 0.8,
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => (
        params.value == null || Number.isNaN(params.value)
          ? '—'
          : `${Number(params.value).toFixed(1)}%`
      ),
    },
  ], [config.AmountFormat]);

  const periodLabel = report?.periodStart && report?.periodEnd
    ? `${report.periodStart} → ${report.periodEnd}`
    : '';

  if (!hasRequiredFilters) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100%' }}>
        <ApHubBreadcrumb variant="harmonate" segments={[{ label: 'Budget vs Payment Intelligence' }]} />
        <Alert severity="info">
          Select a business unit on the dashboard to view Budget vs Payment Intelligence.
        </Alert>
      </Box>
    );
  }

  if (loading && !report) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, flex: 1, minHeight: 0, height: '100%' }}>
      <ApHubBreadcrumb variant="harmonate" segments={[{ label: 'Budget vs Payment Intelligence' }]} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={harmonatePageTitleSx}>
            Budget vs Actuals
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: ht.white }}>
            Sage budget vs Sage paid actuals vs Payment Intelligence invoices
            {periodLabel ? ` · ${periodLabel}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_e, next) => { if (next) setView(next); }}
            sx={{
              '& .MuiToggleButton-root': {
                color: ht.white,
                borderColor: 'rgba(255,255,255,0.35)',
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: ht.white,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                },
              },
            }}
          >
            <ToggleButton value="MTD">MTD</ToggleButton>
            <ToggleButton value="YTD">YTD</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search GL code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            sx={{ ...harmonatePageSearchFieldSx, width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} edge="end" sx={{ color: ht.white }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            size="small"
            onClick={() => loadReport()}
            sx={harmonatePlainIconButtonSx}
            aria-label="Refresh report"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {report && (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mb: 2,
            flexWrap: 'wrap',
            color: ht.white,
            fontSize: '0.875rem',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>Total Budget</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatAmount(report.totalBudget, config.AmountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>Total Actuals (Sage)</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatAmount(report.totalSageActuals, config.AmountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>Total Payment Intelligence</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatAmount(report.totalPaymentIntelligence, config.AmountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>Total Variance (Budget − Actuals)</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatAmount(report.totalVariance, config.AmountFormat)}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, p: 0, overflow: 'hidden' }}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          loading={loading}
          sx={harmonateDataGridSx}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default BudgetVsPaymentIntelligence;
